import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Ticket, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import Button from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { isCartomancerProduct, cartomancerLogoFor } from "../branding";

const RedeemPage = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert(t("redeem.mustLoginAlert"));
      navigate("/login");
      return;
    }

    try {
      setRedeeming(true);
      setResult(null);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/redeem-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code: code.trim() }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: data.message,
          product: data.product,
          access_type: data.access_type,
          access_expires_at: data.access_expires_at,
          access_duration_days: data.access_duration_days,
        });
        setCode("");
      } else {
        let message = t("redeem.invalidCode");
        let ownedAlready = false;
        if (typeof data.detail === "string") {
          message = data.detail;
        } else if (Array.isArray(data.detail)) {
          message = data.detail
            .map((d) => (d && typeof d === "object" ? d.msg || JSON.stringify(d) : String(d)))
            .join(" · ");
        } else if (data.detail && typeof data.detail === "object") {
          if (data.detail.code === "PRODUCT_ALREADY_OWNED") {
            ownedAlready = true;
            message = data.detail.message || t("redeem.productAlreadyOwned");
          } else if (data.detail.code === "PREMIUM_ALREADY_PERMANENT") {
            ownedAlready = true;
            message = data.detail.message || t("redeem.premiumAlreadyPermanent");
          } else {
            message = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
          }
        }
        setResult({ success: false, message, ownedAlready });
      }
    } catch (error) {
      setResult({ success: false, message: t("redeem.connectionError") });
    } finally {
      setRedeeming(false);
    }
  };

  const formatCode = (input) => {
    let cleaned = input.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    cleaned = cleaned.replace(/-/g, "");
    const parts = cleaned.match(/.{1,4}/g) || [];
    return parts.join("-");
  };

  const handleCodeChange = (e) => {
    setCode(formatCode(e.target.value));
  };

  const locale = i18n.language && i18n.language.startsWith("it") ? "it-IT" : "en-US";

  return (
    <div className="min-h-screen py-20 px-6 bg-gradient-to-b from-background to-background/95">
      <div className="container mx-auto max-w-2xl">
        <div className="text-center mb-12">
          <Ticket className="w-20 h-20 text-gold mx-auto mb-6" />
          <h1 className="font-display text-4xl text-gold mb-4" data-testid="redeem-title">
            {t("redeem.title")}
          </h1>
          <p className="text-muted-foreground">{t("redeem.subtitle")}</p>
        </div>

        {result?.success && (
          <Card className="mb-8 border-gold">
            <CardContent className="py-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-gold" />
                </div>
                <h2 className="font-display text-2xl text-gold mb-2">
                  {t("redeem.successTitle")}
                </h2>
                <p className="text-muted-foreground mb-6">{result.message}</p>

                {result.product && (
                  <div className="rune-border bg-card/50 rounded-lg p-6 mb-6">
                    {isCartomancerProduct(result.product.product_id) && (
                      <img
                        src={cartomancerLogoFor(result.product.product_id)}
                        alt={result.product.name}
                        className="mx-auto mb-4 max-h-32 object-contain drop-shadow-[0_6px_16px_rgba(217,178,52,0.35)]"
                        data-testid="redeem-cartomancer-logo"
                      />
                    )}
                    <p className="text-sm text-muted-foreground mb-2">{t("redeem.activated")}</p>
                    <p className="font-heading text-2xl text-gold" data-testid="redeem-product-name">
                      {result.product.name}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {result.access_type === "temporary" && result.access_expires_at ? (
                        <>
                          {t("redeem.temporaryAccess")}{" "}
                          <span className="text-gold font-heading" data-testid="redeem-expires-at">
                            {new Date(result.access_expires_at).toLocaleDateString(locale, {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          {result.access_duration_days
                            ? ` ${t("redeem.temporaryDays", {
                                count: result.access_duration_days,
                              })}`
                            : ""}
                        </>
                      ) : (
                        <>{t("redeem.permanentAccess")}</>
                      )}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <Link to="/app">
                    <Button variant="primary" size="lg" className="w-full">
                      {t("redeem.openCartomancer")}
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button variant="secondary" size="lg" className="w-full">
                      {t("redeem.goDashboard")}
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!result?.success && (
          <Card>
            <CardHeader>
              <CardTitle>{t("redeem.formTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-heading text-foreground mb-2">
                    {t("redeem.codeLabel")}
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder={t("redeem.codePlaceholder")}
                    maxLength="19"
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground text-center text-lg font-mono tracking-wider focus:border-gold focus:outline-none"
                    required
                    disabled={redeeming}
                    data-testid="redeem-code-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {t("redeem.autoFormat")}
                  </p>
                </div>

                {result?.success === false && (
                  <div
                    className={`p-4 border rounded-lg flex items-start gap-3 ${
                      result.ownedAlready
                        ? "bg-gold/10 border-gold"
                        : "bg-ruby/10 border-ruby"
                    }`}
                    data-testid="redeem-error-box"
                  >
                    <AlertCircle
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        result.ownedAlready ? "text-gold" : "text-ruby"
                      }`}
                    />
                    <div>
                      <p
                        className={`text-sm font-heading ${
                          result.ownedAlready ? "text-gold" : "text-ruby"
                        }`}
                      >
                        {result.ownedAlready
                          ? t("redeem.alreadyOwned")
                          : t("redeem.genericError")}
                      </p>
                      <p
                        className="text-sm text-muted-foreground mt-1"
                        data-testid="redeem-error-message"
                      >
                        {result.message}
                      </p>
                      {result.ownedAlready && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {t("redeem.codeNotConsumed")}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {!user && (
                  <div className="p-4 bg-gold/10 border border-gold rounded-lg">
                    <p className="text-sm text-gold">
                      {t("redeem.loginRequired")}{" "}
                      <Link to="/login" className="underline font-heading">
                        {t("redeem.loginNow")}
                      </Link>
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={redeeming || !code || code.length < 19 || authLoading}
                  data-testid="redeem-submit"
                >
                  {redeeming ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t("redeem.verifying")}
                    </>
                  ) : (
                    <>
                      <Ticket className="w-5 h-5 mr-2" />
                      {t("redeem.redeemBtn")}
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 p-4 bg-background/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">
                  <strong className="text-foreground">{t("redeem.howItWorks")}</strong>
                </p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>{t("redeem.how1")}</li>
                  <li>{t("redeem.how2")}</li>
                  <li>{t("redeem.how3")}</li>
                  <li>{t("redeem.how4")}</li>
                </ul>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground mb-3">{t("redeem.noCode")}</p>
                <Link to="/store">
                  <Button variant="gold" size="sm">
                    {t("redeem.buyCartomancer")}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RedeemPage;
